// =============================================================================
// CHARTSSECTION - Conteneur premium pour graphiques chantier
// =============================================================================

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  TrendingUp,
  Gauge,
  PieChart,
  Wallet,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { useChartData } from '@/hooks/useChartData';
import { useBudgetVentile } from '@/hooks/useBudgetVentile';
import { MargeEvolutionChart } from './MargeEvolutionChart';
import { RentabiliteGauge } from './RentabiliteGauge';
import { CoutsBreakdown } from './CoutsBreakdown';
import { BudgetProgress } from './BudgetProgress';
import { BudgetVentilationModal } from '@/components/project/BudgetVentilationModal';

// =============================================================================
// PROPS
// =============================================================================

interface ChartsSectionProps {
  chantierId: string;
  /** Onglet à afficher par défaut */
  defaultTab?: 'evolution' | 'rentabilite' | 'couts' | 'budget';
  /** Ouvrir automatiquement le modal de ventilation */
  autoOpenVentilationModal?: boolean;
  /** Callback appelé après ouverture du modal (pour reset le flag) */
  onVentilationModalOpened?: () => void;
  /** Faire clignoter le bouton "Répartir mon budget" */
  highlightBudgetButton?: boolean;
  /** Callback appelé quand le bouton budget est cliqué */
  onBudgetButtonClicked?: () => void;
}

// =============================================================================
// SKELETON DE CHARGEMENT PREMIUM
// =============================================================================

function ChartSkeleton() {
  return (
    <div className="h-[400px] flex items-center justify-center">
      <div className="space-y-6 w-full px-8">
        <div className="flex justify-center">
          <Skeleton className="h-6 w-48" />
        </div>
        <Skeleton className="h-56 w-full rounded-xl" />
        <div className="flex justify-center gap-6">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ÉTAT ERREUR
// =============================================================================

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="h-[400px] flex flex-col items-center justify-center text-muted-foreground">
      <AlertCircle className="h-14 w-14 mb-4 opacity-40 text-destructive" />
      <p className="text-sm mb-4">{message}</p>
      <Button variant="outline" size="sm" onClick={onRetry} className="gap-2">
        <RefreshCw className="h-4 w-4" />
        Réessayer
      </Button>
    </div>
  );
}

// =============================================================================
// ONGLETS CONFIGURATION
// =============================================================================

const TABS = [
  {
    id: 'budget',
    label: 'Budget',
    shortLabel: 'Budg.',
    icon: Wallet,
    description: 'Consommation budgétaire',
  },
  {
    id: 'rentabilite',
    label: 'Rentabilité',
    shortLabel: 'Rent.',
    icon: Gauge,
    description: 'Performance financière',
  },
  {
    id: 'couts',
    label: 'Répartition',
    shortLabel: 'Coûts',
    icon: PieChart,
    description: 'Ventilation des coûts',
  },
  {
    id: 'evolution',
    label: 'Évolution',
    shortLabel: 'Évol.',
    icon: TrendingUp,
    description: 'Suivi temporel des métriques',
  },
] as const;

type TabId = typeof TABS[number]['id'];

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function ChartsSection({
  chantierId,
  defaultTab,
  autoOpenVentilationModal,
  onVentilationModalOpened,
  highlightBudgetButton,
  onBudgetButtonClicked,
}: ChartsSectionProps) {
  const [activeTab, setActiveTab] = useState<TabId>(defaultTab || 'budget');
  const [showVentilationModal, setShowVentilationModal] = useState(false);

  // État pour plier/déplier les graphiques (persisté dans localStorage)
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    const saved = localStorage.getItem('charts-expanded');
    return saved !== null ? JSON.parse(saved) : true; // Par défaut: déplié
  });

  // Persister l'état d'expansion dans localStorage
  useEffect(() => {
    localStorage.setItem('charts-expanded', JSON.stringify(isExpanded));
  }, [isExpanded]);

  // Ouvrir automatiquement le modal de ventilation si demandé
  useEffect(() => {
    if (autoOpenVentilationModal && !showVentilationModal) {
      // Attendre que l'onglet budget soit visible
      setActiveTab('budget');
      // Petit délai pour que l'UI se mette à jour
      const timer = setTimeout(() => {
        setShowVentilationModal(true);
        onVentilationModalOpened?.();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoOpenVentilationModal, showVentilationModal, onVentilationModalOpened]);

  // Activer l'onglet budget quand le highlight est demandé (flow checklist)
  useEffect(() => {
    if (highlightBudgetButton && activeTab !== 'budget') {
      setActiveTab('budget');
    }
  }, [highlightBudgetButton]);

  // Données des graphiques
  const {
    chantierNom,
    evolutionData,
    rentabiliteData,
    budgetData,
    coutsData,
    isLoading,
    error,
    refetch,
  } = useChartData(chantierId);

  // Données de ventilation du budget
  const {
    data: budgetVentileData,
    currentVentilation,
    budgetTotal,
    saveVentilation,
    clearVentilation,
  } = useBudgetVentile(chantierId);

  // Rendu du graphique selon l'onglet actif
  const renderChart = () => {
    if (isLoading) return <ChartSkeleton />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;

    switch (activeTab) {
      case 'evolution':
        return <MargeEvolutionChart data={evolutionData} />;
      case 'rentabilite':
        return <RentabiliteGauge data={rentabiliteData} />;
      case 'couts':
        return <CoutsBreakdown data={coutsData} />;
      case 'budget':
        return (
          <BudgetProgress
            data={budgetData}
            budgetVentile={budgetVentileData}
            onConfigureVentile={() => {
              setShowVentilationModal(true);
              // Marquer l'étape checklist comme complétée quand le user clique sur le bouton
              localStorage.setItem('b8ild_budget_ventile_clicked', 'true');
              // Émettre un événement pour rafraîchir la checklist
              window.dispatchEvent(new Event('b8ild-checklist-update'));
              // Callback pour arrêter le clignotement
              onBudgetButtonClicked?.();
            }}
            highlightButton={highlightBudgetButton}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card className="w-full overflow-hidden border-border/50 bg-gradient-to-b from-card to-card/80">
        {/* En-tête premium */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-xl font-bold text-foreground tracking-tight">
              Analyse financière
            </h2>
            <div className="flex items-center gap-4">
              {chantierNom && (
                <span className="text-sm text-muted-foreground">
                  {chantierNom}
                </span>
              )}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/30 border border-border/50">
                <span className="text-xs font-medium text-muted-foreground">
                  Graphiques
                </span>
                <Switch
                  checked={isExpanded}
                  onCheckedChange={setIsExpanded}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Visualisation des indicateurs clés du chantier
          </p>
        </div>

        {/* Onglets premium */}
        {isExpanded && (
        <div className="px-6 pb-4">
          <div className="flex gap-2 p-1.5 bg-muted/30 rounded-xl">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'relative flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg',
                    'text-sm transition-all duration-200',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    isActive
                      ? 'bg-primary text-white shadow-lg shadow-primary/30 border-2 border-primary font-bold'
                      : 'text-muted-foreground hover:text-foreground hover:bg-card/50 font-medium border-2 border-transparent'
                  )}
                >
                  <Icon className={cn(
                    'h-4 w-4 flex-shrink-0 transition-colors',
                    isActive && 'text-white'
                  )} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.shortLabel}</span>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Contenu du graphique */}
        {isExpanded && (
        <CardContent className="p-6 pt-0">
          <div
            className={cn(
              'min-h-[420px] rounded-xl',
              'bg-gradient-to-br from-background/50 to-background',
              'border border-border/30',
              'p-4'
            )}
          >
            {renderChart()}
          </div>
        </CardContent>
        )}
      </Card>

      {/* Modal de ventilation du budget */}
      <BudgetVentilationModal
        isOpen={showVentilationModal}
        onClose={() => setShowVentilationModal(false)}
        budgetTotal={budgetTotal || budgetData.total}
        currentVentilation={currentVentilation}
        depensesActuelles={budgetVentileData?.categories}
        onSave={saveVentilation}
        onClear={clearVentilation}
      />
    </>
  );
}
